<template>
    <div class="kiosk-chat-view d-flex flex-column flex-grow-1 overflow-hidden">
      <!-- Header -->
      <header class="kiosk-chat-header bg-secondary text-white p-3 d-flex justify-content-between align-items-center shadow-sm">
        <h3 class="kiosk-chat-view-title h5 mb-0">{{ chatTitle }}</h3>
        <button @click="$emit('end-session')" class="btn btn-sm btn-outline-light kiosk-chat-end-session-button" aria-label="End current conversation and start new">
          End & Start New
        </button>
      </header>

      <!-- Messages Area -->
      <div class="kiosk-chat-messages-area flex-grow-1 p-3" ref="messageContainerRef" style="overflow-y: auto;">
        <div v-if="messages.length === 0 && !isAiLoading" class="kiosk-no-messages text-center text-muted h-100 d-flex align-items-center justify-content-center">
          <p>Ask me anything about the event!</p>
        </div>
        <MessageItem v-for="message in messages" :key="message.id" :message="message" />
        <!-- AI Loading placeholder if it's the next message to appear -->
         <div v-if="isAiLoading && (messages.length === 0 || messages[messages.length - 1].sender === 'user')" class="kiosk-message-item d-flex mb-3 justify-content-start">
            <div class="kiosk-message-content card bg-light kiosk-ai-message" style="max-width: 75%; border-radius: 1.25rem;">
                <div class="card-body p-3">
                    <div class="kiosk-ai-typing-indicator my-2">
                        <span class="kiosk-typing-dot">.</span><span class="kiosk-typing-dot">.</span><span class="kiosk-typing-dot">.</span>
                    </div>
                </div>
            </div>
        </div>
      </div>

      <!-- Input Area -->
      <footer class="kiosk-chat-input-zone bg-white p-3 border-top">
        <form @submit.prevent="sendMessage" class="d-flex align-items-stretch">
          <textarea
            v-model="internalNewMessage"
            @keyup.enter.exact="sendMessage"
            @input="handleInput"
            :placeholder="placeholderText"
            class="form-control kiosk-chat-textarea mr-2"
            rows="2"
            style="resize: none; font-size: 1.1rem;"
            ref="messageInputRef"
            :disabled="isAiLoading"
            aria-label="Type your message"
          ></textarea>
          <button type="submit" class="btn btn-primary kiosk-chat-send-button" :disabled="!internalNewMessage.trim() || isAiLoading" style="min-width: 80px;">
            <svg v-if="!isAiLoading" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
            <svg v-else class="kiosk-loading-spinner" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z" opacity=".25"/><path d="M10.14,1.16a11,11,0,0,0-9,8.92A1.59,1.59,0,0,0,2.46,12,1.52,1.52,0,0,0,4.11,10.7a8,8,0,0,1,6.66-6.61A1.42,1.42,0,0,0,12,2.69h0A1.57,1.57,0,0,0,10.14,1.16Z"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.75s" repeatCount="indefinite"/></path></svg>
          </button>
        </form>
      </footer>
    </div>    
</template>

<script>
import MessageItem from './MessageItem.vue';

export default {
  components: {
    MessageItem,
  },
  props: {
    messages: {
      type: Array,
      required: true,
    },
    currentNewMessage: { // For .sync modifier
      type: String,
      required: true,
    },
    isAiLoading: {
      type: Boolean,
      default: false,
    },
    chatTitle: {
      type: String,
      default: 'Chat Assistant',
    },
    placeholderText: {
      type: String,
      default: 'Type your question here...',
    }
  },
  data() {
    return {
      internalNewMessage: this.currentNewMessage,
    };
  },
  watch: {
    currentNewMessage(val) {
      this.internalNewMessage = val;
    },
    internalNewMessage(val) {
      this.$emit('update:currentNewMessage', val); // Keep parent synced
      this.$emit('user-activity'); // Notify parent of typing
    },
    messages: {
      handler() {
        this.scrollToBottom();
      },
      deep: true,
    },
  },
  methods: {
    sendMessage() {
      if (this.internalNewMessage.trim() && !this.isAiLoading) {
        this.$emit('send-message', this.internalNewMessage.trim());
        this.internalNewMessage = ''; // Clear local input
        this.$nextTick(() => {
            if (this.$refs.messageInputRef) {
                this.$refs.messageInputRef.focus();
            }
        });
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messageContainerRef;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    focusInput() {
        this.$nextTick(() => {
            if (this.$refs.messageInputRef) {
                this.$refs.messageInputRef.focus();
            }
        });
    },
    handleInput() {
        this.$emit('user-activity');
    }
  },
  mounted() {
    this.focusInput();
    this.scrollToBottom();
  },
};

</script>
